import React, { useState, useEffect, useCallback } from 'react'
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Paper,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Home,
  BookOnline,
  TrendingUp,
  Add,
  MoreVert,
  CalendarToday,
  AttachMoney,
  Visibility,
  Edit,
  CheckCircle,
  Warning,
  Schedule,
} from '@mui/icons-material'
import { Link, useNavigate } from 'react-router-dom'
import { SidebarLayout } from '@/components/Layout'
import { useAuth } from '@/contexts/AuthContext'
import { ListingsService } from '@/utils/listings-service'
import { BookingsService } from '@/utils/bookings-service'
// import { PendingImportsPanel } from '@/components/Listings'
import { ExpenseSummaryWidget } from '@/components/Expenses'
import { BecomeHostButton } from '@/components/common/BecomeHostButton'
import { ReviewSummaryWidget } from '@/components/Reviews/ReviewSummaryWidget'
import { Listing, Booking } from '@/types'

export default function Dashboard() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const { userProfile, getUserState } = useAuth()
  const [listings, setListings] = useState<Listing[]>([])
  const [bookings, setBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedListing, setSelectedListing] = useState<string | null>(null)
  
  const userState = getUserState()

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Only fetch listings data if user has host role
      const isUserHost = userProfile?.roles.includes('host')
      
      if (isUserHost) {
        // Fetch both listings and bookings for hosts
        const [listingsData, bookingsData] = await Promise.all([
          ListingsService.getListings(),
          BookingsService.getBookings()
        ])
        setListings(listingsData || [])
        setBookings(bookingsData || [])
      } else {
        // For guest-only users, only fetch bookings (their travel bookings)
        const bookingsData = await BookingsService.getBookings()
        setListings([]) // No listings for guest-only users
        setBookings(bookingsData || [])
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error)
      setError('Failed to load dashboard data. Please try again.')
      // Set empty arrays as fallback to prevent crashes
      setListings([])
      setBookings([])
    } finally {
      setLoading(false)
    }
  }, [userProfile?.roles])

  useEffect(() => {
    // Redirect employees to admin dashboard
    if (userProfile?.userGroup === 'employees') {
      navigate('/admin/dashboard', { replace: true })
      return
    }
    
    loadDashboardData()
  }, [userProfile, navigate, loadDashboardData])

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, listingId: string) => {
    setAnchorEl(event.currentTarget)
    setSelectedListing(listingId)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedListing(null)
  }

  // Calculate dashboard metrics
  const totalListings = listings.length
  const publishedListings = listings.filter(l => l.status === 'published').length
  const draftListings = listings.filter(l => l.status === 'draft').length
  const totalBookings = bookings.length
  const confirmedBookings = bookings.filter(b => b.status === 'confirmed').length
  const pendingBookings = bookings.filter(b => b.status === 'pending').length
  // Calculate revenue for current month
  const currentMonth = new Date().getMonth()
  const currentYear = new Date().getFullYear()
  const totalRevenue = bookings
    .filter(b => {
      const bookingDate = new Date(b.startDate)
      return b.status === 'confirmed' && 
             b.paymentStatus === 'paid' &&
             bookingDate.getMonth() === currentMonth &&
             bookingDate.getFullYear() === currentYear
    })
    .reduce((sum, b) => sum + b.totalPrice, 0)

  // Calculate occupancy rate based on actual bookings
  const calculateOccupancyRate = () => {
    if (totalListings === 0) return 0
    
    const currentDate = new Date()
    const thirtyDaysAgo = new Date(currentDate.getTime() - (30 * 24 * 60 * 60 * 1000))
    
    // Get bookings from the last 30 days
    const recentBookings = bookings.filter(booking => {
      const bookingDate = new Date(booking.startDate)
      return bookingDate >= thirtyDaysAgo && bookingDate <= currentDate && booking.status === 'confirmed'
    })
    
    // Calculate total booked nights
    const totalBookedNights = recentBookings.reduce((sum, booking) => {
      const startDate = new Date(booking.startDate)
      const endDate = new Date(booking.endDate)
      const nights = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
      return sum + nights
    }, 0)
    
    // Calculate total available nights (30 days * number of published listings)
    const totalAvailableNights = publishedListings * 30
    
    if (totalAvailableNights === 0) return 0
    
    return Math.round((totalBookedNights / totalAvailableNights) * 100)
  }

  const occupancyRate = calculateOccupancyRate()

  // Recent bookings (last 5)
  const recentBookings = bookings
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'success'
      case 'draft': return 'warning'
      case 'paused': return 'error'
      case 'confirmed': return 'success'
      case 'pending': return 'warning'
      case 'cancelled': return 'error'
      default: return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'published': return <CheckCircle />
      case 'draft': return <Edit />
      case 'paused': return <Warning />
      case 'confirmed': return <CheckCircle />
      case 'pending': return <Schedule />
      default: return <Schedule />
    }
  }

  if (loading) {
    return (
      <SidebarLayout>
        <Box sx={{ width: '100%', mt: 4 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
            Loading dashboard data...
          </Typography>
        </Box>
      </SidebarLayout>
    )
  }

  if (error) {
    return (
      <SidebarLayout>
        <Box sx={{ mt: 4 }}>
          <Alert 
            severity="error" 
            action={
              <Button color="inherit" size="small" onClick={loadDashboardData}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      </SidebarLayout>
    )
  }

  // Guest-only dashboard sections
  const renderGuestOnlyDashboard = () => (
    <>
      {/* Welcome Section with Become a Host CTA */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Welcome to BnBCart
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Discover amazing places to stay and manage your travel bookings.
        </Typography>
        <BecomeHostButton variant="cta" />
      </Box>

      {/* Guest Booking Management Section */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  My Bookings
                </Typography>
                <Button
                  component={Link}
                  to="/my-trips"
                  size="small"
                  endIcon={<CalendarToday />}
                >
                  View All
                </Button>
              </Box>

              {bookings.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <BookOnline sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No bookings yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Start exploring amazing places to stay
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<BookOnline />}
                  >
                    Browse Properties
                  </Button>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {bookings.slice(0, 3).map((booking, index) => {
                    const listing = listings.find(l => l.listingId === booking.listingId)
                    return (
                      <React.Fragment key={booking.bookingId}>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: getStatusColor(booking.status) + '.main' }}>
                              {getStatusIcon(booking.status)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {listing?.title || 'Property Booking'}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {BookingsService.formatDateRange(booking.startDate, booking.endDate)}
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                  ${booking.totalPrice}
                                </Typography>
                              </Box>
                            }
                          />
                          <Chip
                            label={booking.status}
                            size="small"
                            color={getStatusColor(booking.status) as any}
                          />
                        </ListItem>
                        {index < Math.min(bookings.length, 3) - 1 && <Divider />}
                      </React.Fragment>
                    )
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Travel Preferences and Profile Management */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                Travel Preferences
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Complete your profile to get personalized recommendations
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={userProfile?.name ? 60 : 20} 
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Profile {userProfile?.name ? '60' : '20'}% complete
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    fullWidth
                    component={Link}
                    to="/profile"
                    startIcon={<Edit />}
                  >
                    Complete Profile
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    fullWidth
                    component={Link}
                    to="/chat"
                    startIcon={<BookOnline />}
                  >
                    Message History
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions for Guests */}
      <Paper sx={{ p: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<BookOnline />}
              sx={{ 
                borderColor: 'white', 
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Browse Properties
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              component={Link}
              to="/my-trips"
              startIcon={<CalendarToday />}
              sx={{ 
                borderColor: 'white', 
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              My Trips
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              component={Link}
              to="/profile"
              startIcon={<Edit />}
              sx={{ 
                borderColor: 'white', 
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Edit Profile
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <BecomeHostButton size="medium" />
          </Grid>
        </Grid>
      </Paper>
    </>
  )

  return (
    <SidebarLayout>
      {userState === 'guest-only' ? renderGuestOnlyDashboard() : (
        <>
          {/* Header */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Welcome back! Here's an overview of your {userState === 'guest-and-host' ? 'hosting business and travel bookings' : 'BnB business'}.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              component={Link}
              to="/listings/new"
              sx={{ mt: { xs: 2, sm: 0 } }}
            >
              Add Property
            </Button>
          </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: '#fef3e8' }}>
                <Home sx={{ color: '#f59842' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" fontWeight={700}>{totalListings}</Typography>
                <Typography variant="body2" color="text.secondary">Properties</Typography>
              </Box>
              <Chip label={`${publishedListings} published`} size="small" color="success" variant="outlined" />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: '#fef3e8' }}>
                <BookOnline sx={{ color: '#f59842' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" fontWeight={700}>{totalBookings}</Typography>
                <Typography variant="body2" color="text.secondary">Bookings</Typography>
              </Box>
              <Chip label={`${confirmedBookings} confirmed`} size="small" color="success" variant="outlined" />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: '#fef3e8' }}>
                <AttachMoney sx={{ color: '#f59842' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" fontWeight={700}>${totalRevenue.toLocaleString()}</Typography>
                <Typography variant="body2" color="text.secondary">Revenue</Typography>
              </Box>
              <Chip label="This month" size="small" variant="outlined" />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: '#fef3e8' }}>
                <Visibility sx={{ color: '#f59842' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" fontWeight={700}>{occupancyRate}%</Typography>
                <Typography variant="body2" color="text.secondary">Occupancy</Typography>
              </Box>
              <Chip label="Last 30 days" size="small" variant="outlined" />
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Import Status Summary - Temporarily Disabled */}
      {/* <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
          Import Status
        </Typography>
        <PendingImportsPanel 
          onImportResume={(importId) => {
            // Navigate to manage imports page to continue the import
            navigate('/manage-imports')
          }}
          onImportCancel={(importId) => {
            console.log('Import cancelled:', importId)
          }}
          onImportComplete={(session) => {
            // Navigate to the newly created listing
            if (session.listingId) {
              navigate(`/listings/${session.listingId}`)
            }
          }}
          showHeader={false}
          maxItems={3}
        />
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="outlined"
            component={Link}
            to="/manage-imports"
            size="small"
          >
            View All Imports
          </Button>
        </Box>
      </Box> */}

      <Grid container spacing={4}>
        {/* Recent Listings */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Your Properties
                </Typography>
                <Button
                  component={Link}
                  to="/listings"
                  size="small"
                  endIcon={<TrendingUp />}
                >
                  View All
                </Button>
              </Box>
              
              {listings.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Home sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No properties yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Create your first property listing to get started
                  </Typography>
                  <Button
                    variant="contained"
                    component={Link}
                    to="/listings/new"
                    startIcon={<Add />}
                  >
                    Add Your First Property
                  </Button>
                </Box>
              ) : (
                <List>
                  {listings.map((listing, index) => (
                    <React.Fragment key={listing.listingId}>
                      <ListItem
                        sx={{
                          px: 0,
                          '&:hover': {
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                          },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={listing.photos[0]}
                            sx={{ width: 56, height: 56, mr: 2 }}
                          >
                            <Home />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {listing.title}
                              </Typography>
                              <Chip
                                label={listing.status}
                                size="small"
                                color={getStatusColor(listing.status) as any}
                                icon={getStatusIcon(listing.status)}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {listing.address.city}, {listing.address.state} • {listing.maxGuests} guests
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                ${listing.pricing.basePrice}/night
                              </Typography>
                            </Box>
                          }
                        />
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, listing.listingId)}
                          size="small"
                        >
                          <MoreVert />
                        </IconButton>
                      </ListItem>
                      {index < listings.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Expense Summary Widget */}
        <Grid item xs={12} lg={6}>
          <ExpenseSummaryWidget />
        </Grid>
      </Grid>

      {/* Second Row */}
      <Grid container spacing={4} sx={{ mt: 2 }}>
        {/* Recent Host Bookings */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Recent Host Bookings
                </Typography>
                <Button
                  component={Link}
                  to="/my-bookings"
                  size="small"
                  endIcon={<CalendarToday />}
                >
                  View All
                </Button>
              </Box>

              {recentBookings.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <BookOnline sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No bookings yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bookings will appear here once guests start reserving your properties
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {recentBookings.map((booking, index) => {
                    const listing = listings.find(l => l.listingId === booking.listingId)
                    return (
                      <React.Fragment key={booking.bookingId}>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: getStatusColor(booking.status) + '.main' }}>
                              {getStatusIcon(booking.status)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {listing?.title || 'Unknown Property'}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {BookingsService.formatDateRange(booking.startDate, booking.endDate)}
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                  ${booking.totalPrice}
                                </Typography>
                              </Box>
                            }
                          />
                          <Chip
                            label={booking.status}
                            size="small"
                            color={getStatusColor(booking.status) as any}
                          />
                        </ListItem>
                        {index < recentBookings.length - 1 && <Divider />}
                      </React.Fragment>
                    )
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Additional Dashboard Space */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    fullWidth
                    component={Link}
                    to="/expenses"
                    startIcon={<AttachMoney />}
                  >
                    Manage Expenses
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    fullWidth
                    component={Link}
                    to="/listings/new"
                    startIcon={<Add />}
                  >
                    Add New Property
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    fullWidth
                    component={Link}
                    to="/calendar"
                    startIcon={<CalendarToday />}
                  >
                    View Calendar
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Third Row - Reviews */}
      <Grid container spacing={4} sx={{ mt: 2 }}>
        {/* Review Summary Widget */}
        <Grid item xs={12} lg={6}>
          <ReviewSummaryWidget />
        </Grid>

        {/* Additional space for future widgets */}
        <Grid item xs={12} lg={6}>
          {/* This space can be used for other widgets in the future */}
        </Grid>
      </Grid>

      {/* Guest Bookings Section for Guest & Host Users */}
      {userState === 'guest-and-host' && (
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    My Travel Bookings
                  </Typography>
                  <Button
                    size="small"
                    endIcon={<BookOnline />}
                  >
                    Browse Properties
                  </Button>
                </Box>

                {/* This would show guest bookings - for now showing placeholder */}
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <BookOnline sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No travel bookings yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Book your next getaway and manage your travel plans here
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<BookOnline />}
                  >
                    Browse Properties
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Quick Actions */}
      <Paper sx={{ p: 3, mt: 4, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              component={Link}
              to="/listings/new"
              startIcon={<Add />}
              sx={{ 
                borderColor: 'white', 
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Add Property
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              component={Link}
              to="/my-bookings"
              startIcon={<BookOnline />}
              sx={{ 
                borderColor: 'white', 
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              View Bookings
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              component={Link}
              to="/profile"
              startIcon={<Edit />}
              sx={{ 
                borderColor: 'white', 
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Edit Profile
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              component={Link}
              to="/expenses"
              startIcon={<AttachMoney />}
              sx={{ 
                borderColor: 'white', 
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Expense Management
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Menu for listing actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && Boolean(selectedListing)}
        onClose={handleMenuClose}
      >
        {selectedListing && (
          <>
            <MenuItem
              component={Link}
              to={`/listings/${selectedListing}`}
              onClick={handleMenuClose}
            >
              <Edit sx={{ mr: 1 }} />
              Edit
            </MenuItem>
            <MenuItem
              component={Link}
              to={`/listings/${selectedListing}`}
              onClick={handleMenuClose}
            >
              <Visibility sx={{ mr: 1 }} />
              View
            </MenuItem>
          </>
        )}
      </Menu>
        </>
      )}
    </SidebarLayout>
  )
}